import { Permissions } from '../../../../models/enums/permissions';
import * as React from 'react';
import { getQuantity, removeTrailing } from "../../../../modules/inventory/models/utils";
import { PageBody } from '../../../../components/page-components/PageBody';
import { HasPermission } from '../../../../containers/authorization-helpers/HasPermission';
import { formatCurrency } from '../../../../utils/currency-formatter';
import { InventorySvc } from '../../../../shared/services/inventory-service';
import { InventoryPhysicalAdjustmentInfo, InventoryPhysicalAdjustment, InventoryPhysicalAdjustmentGroup } from '../../../../modules/inventory/models/inventory-physical-adjustments';
import { StatusCode } from '../../../../utils/api';
import { ApiEndpoints, Routes } from '../../../../utils/app-constants';
import { navigateToPage } from '../../../../utils/history';
import { Table } from '../../../../components/tables/Table';
import { AutoComplete } from '../../../../containers/autocomplete/AutoComplete';
import { InventoryPartAutocompleteItem } from '../../../../modules/inventory/models/inventory-part-autocomplete-item';
import { useDispatch, useStore } from 'react-redux';
import { AutoCompleteItem } from '../../../../models/utilities/autocomplete-item';
import { IApplicationState } from '../../../../store/IApplicationState';
import { ServiceCenter } from '../../../../models/utilities/service-center';
import { PhysicalInventoryStatus } from '../../../../modules/inventory/models/physical-inventory-enums';

interface IProps {
    location: any;
}

export const InventoryPhysicalAdjustmentsScene = React.forwardRef((props: IProps, ref) => {

    const store = useStore();
    const dispatch = useDispatch();
    const [service] = React.useState(new InventorySvc());
    const [adjustmentInfo, setAdjustmentInfo] = React.useState(new InventoryPhysicalAdjustmentInfo());
    const [newPart, setNewPart] = React.useState<InventoryPartAutocompleteItem | null>(null);
    const [serviceCenters] = React.useState(() => {
        var sc = (store.getState() as IApplicationState).utility.appServiceCenters;
        return sc.filter((sc: ServiceCenter) => sc.name !== 'ALL');
    });



    // initialization 
    React.useEffect(() => {
        getPageData(getReportId());
    }, [props.location]);

    React.useImperativeHandle(ref, () => ({
        goBack() {
            goBack();
        }
    }))

    function goBack() {
        var reportId = getReportId();
        navigateToPage(Routes.INVENTORY_PHYSICAL_DETAILS_CONTROLGROUPS.formattify(reportId));
    }

    function disableEdit(): boolean {
        return adjustmentInfo.reportStatus == PhysicalInventoryStatus.complete;
    }

    function getReportId() {
        const opts = props.location.pathname.split('/');
        return opts.length > 4 ? opts[4] : null;
    }

    function save() {
        var tmpInfo = { ...adjustmentInfo };

        for (let group of tmpInfo.adjustmentGroups) {
            for (let adj of group.adjustments) {
                setServerQuantities(adj);
            }
        }
        var reportId = getReportId();

        service.updateAdjustments(reportId, tmpInfo)
            .then((result) => {
                if (result.status === StatusCode.Success) {
                    getPageData(reportId);
                }
            });
    }

    function addAdjustmentGroup() {
        var tmpInfo = { ...adjustmentInfo };

        var group = new InventoryPhysicalAdjustmentGroup();
        tmpInfo.adjustmentGroups.push(group);
        setAdjustmentInfo(tmpInfo);
    }

    function getButtons() {
        return <HasPermission requiredPermissions={[
            Permissions.InventoryPhysicalAdjustmentsEdit
        ]}>
            <button className="btn attention" onClick={save} disabled={disableEdit()}>
                SAVE
            </button>
            <button className="btn attention" onClick={addAdjustmentGroup} disabled={disableEdit()}>
                ADD ADJUSTMENT GROUP    
            </button>
        </HasPermission>;
    }

    async function getPageData(reportId: string) {
        if (reportId) {
            const result = await service.getAdjustmentData(reportId);
            if (result.status === StatusCode.Success) {
                for (let group of result.data.adjustmentGroups) {
                    for (let adj of group.adjustments) {
                        setViewQuantities(adj);
                    }
                }
                setAdjustmentInfo(result.data);
            }
        }
        else {
            goBack();
        }
    }

    function setViewQuantities(adjustment: InventoryPhysicalAdjustment) {
        adjustment.quantity1 = adjustment.quantities[0];
        adjustment.quantity2 = adjustment.quantities[1];
        adjustment.quantity3 = adjustment.quantities[2];
        adjustment.quantity4 = adjustment.quantities[3];
        adjustment.quantity5 = adjustment.quantities[4];
        adjustment.quantity6 = adjustment.quantities[5];
    }

    function setServerQuantities(adjustment: InventoryPhysicalAdjustment) {
        var result = [
            adjustment.quantity1,
            adjustment.quantity2,
            adjustment.quantity3,
            adjustment.quantity4,
            adjustment.quantity5,
            adjustment.quantity6
        ];

        removeTrailing(result);
        adjustment.quantities = result;
    }

    function onPartSelected(e: AutoCompleteItem) {
        if (e?.extraData?.id) {
            setNewPart(e.extraData as InventoryPartAutocompleteItem)
        }
    }

    function addAdjustment(groupIndex: number) {
        
        if (newPart) {            
            var tmpInfo = { ...adjustmentInfo };

            var grp = tmpInfo.adjustmentGroups[groupIndex];

            var adjustment = new InventoryPhysicalAdjustment();
            adjustment.partNumber = newPart.partNumber;
            adjustment.serviceCenterId = "MAIN";
            adjustment.description = newPart.description;
            adjustment.unitOfMeasure = newPart.unitOfMeasure;

            grp.adjustments.push(adjustment);

            setAdjustmentInfo(tmpInfo);
            setNewPart(null);
        }        
    }

    function removeAdjustment(groupIndex: number, adjustmentIndex: number) {
        var tmpInfo = { ...adjustmentInfo };

        var grp = tmpInfo.adjustmentGroups[groupIndex];
        if (grp) {
            var adjustment = grp.adjustments[adjustmentIndex];
            if (adjustment) {
                grp.adjustments.splice(adjustmentIndex, 1);
            }
        }
        setAdjustmentInfo(tmpInfo);
    }

    function handleServiceCenterChange(e: any, groupIndex: number, adjustmentIndex: number) {
        e.preventDefault();
        var tmpInfo = { ...adjustmentInfo };
        var grp = tmpInfo.adjustmentGroups[groupIndex];
        if (grp) {
            var adjustment = grp.adjustments[adjustmentIndex];
            if (adjustment) {
                adjustment.serviceCenterId = e.target.value;
            }
        }
        setAdjustmentInfo(tmpInfo);
    }
    function handleBlur(e: React.ChangeEvent<HTMLInputElement>, groupIndex: number, adjustmentIndex: number) {
        if (e?.target) {
            var tmpInfo = { ...adjustmentInfo };

            var grp = tmpInfo.adjustmentGroups[groupIndex];
            var adjustment = grp.adjustments[adjustmentIndex];

            if (e.target.name == "quantity1") {
                adjustment.quantity1 = getQuantity(adjustment.quantity1, true);
            }
            if (e.target.name == "quantity2") {
                adjustment.quantity2 = getQuantity(adjustment.quantity2, true);
            }
            if (e.target.name == "quantity3") {
                adjustment.quantity3 = getQuantity(adjustment.quantity3, true);
            }
            if (e.target.name == "quantity4") {
                adjustment.quantity4 = getQuantity(adjustment.quantity4, true);
            }
            if (e.target.name == "quantity5") {
                adjustment.quantity5 = getQuantity(adjustment.quantity5, true);
            }
            if (e.target.name == "quantity6") {
                adjustment.quantity6 = getQuantity(adjustment.quantity6, true);
            }

            setAdjustmentInfo(tmpInfo);
        }
    }


    function handleChange(e: React.ChangeEvent<HTMLInputElement>, groupIndex: number, adjustmentIndex: number) {        
        if (e?.target) {
            var tmpInfo = { ...adjustmentInfo };

            var grp = tmpInfo.adjustmentGroups[groupIndex];
            var adjustment = grp.adjustments[adjustmentIndex];

            if (e.target.name == "quantity1") {
                adjustment.quantity1 = e.target.value;
            }
            if (e.target.name == "quantity2") {
                adjustment.quantity2 = e.target.value;
            }
            if (e.target.name == "quantity3") {
                adjustment.quantity3 = e.target.value;
            }
            if (e.target.name == "quantity4") {
                adjustment.quantity4 = e.target.value;
            }
            if (e.target.name == "quantity5") {
                adjustment.quantity5 = e.target.value;
            }
            if (e.target.name == "quantity6") {
                adjustment.quantity6 = e.target.value;
            }

            setAdjustmentInfo(tmpInfo);
        }        
    }

    function handleChangeReason(e: React.ChangeEvent<HTMLInputElement>, groupIndex: number) {
        if (e?.target?.value) {
            var tmpInfo = { ...adjustmentInfo };
            var grp = tmpInfo.adjustmentGroups[groupIndex];
            if (grp) {
                grp.reason = e.target.value;
            }
            setAdjustmentInfo(tmpInfo);
        }
    }

    function removeAdjustmentGroup(groupIndex: number) {
        var tmpInfo = { ...adjustmentInfo };
        var grp = tmpInfo.adjustmentGroups[groupIndex];
        if(grp) {
            tmpInfo.adjustmentGroups.splice(groupIndex, 1);
        }
        setAdjustmentInfo(tmpInfo);
    }

    return (
        <PageBody>

            <div className="row">
                <div className="col">
                    {getButtons()}
                </div>
            </div>
            <div className="row">
                <div className="col-3">
                </div>
                <div className="col-3">
                    <span className="invnetory-label">TOTAL ADJUSTMENTS</span>
                    <span className="invnetory-value">{adjustmentInfo.totalAdjustments}</span>
                </div>
                <div className="col-3">
                    <span className="invnetory-label">TOTAL VALUE</span>
                    {
                        adjustmentInfo.totalValue === undefined
                            ? <span></span>
                            : <span className="invnetory-value"> {formatCurrency(Number(adjustmentInfo.totalValue))} </span>
                    }
                </div>
                <div className="col-3">
                </div>
            </div>
            {
                adjustmentInfo.adjustmentGroups.map((grp: InventoryPhysicalAdjustmentGroup, grpIdx: number) => {
                    return <div className="row">
                        <div className="col">

                            <div className="row">
                                <div className="col-10">
                                    <span className="invnetory-label left-align">REASON</span>
                                    <input
                                        id="reason"
                                        disabled={disableEdit()}
                                        className="form-control"
                                        type="text"
                                        name="reason"
                                        value={grp.reason}
                                        onChange={(e) => handleChangeReason(e, grpIdx)}
                                    />
                                </div>
                                <div className="col-2">
                                    <button className="btn delete"
                                        disabled={disableEdit()}
                                        onClick={() => removeAdjustmentGroup(grpIdx)}>
                                        DELETE
                                    </button>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col">
                                    <Table
                                        header={
                                            ["PART #",
                                                "SERVICE CENTER",
                                                "DESCRIPTION",
                                                "UNIT",
                                                'QTY 1',
                                                'QTY 2',
                                                'QTY 3',
                                                'QTY 4',
                                                'QTY 5',
                                                'QTY 6'
                                            ]}
                                        noResultsMessage="No adjustments in group.">
                                        {
                                            grp.adjustments.map((adj: InventoryPhysicalAdjustment, adjIdx: number) => {
                                                return <tr>
                                                    <td style={{ width: '14%' }}>
                                                        <button
                                                            id="remove-item"
                                                            onClick={(e) => removeAdjustment(grpIdx, adjIdx)}
                                                            type="button"
                                                            disabled={disableEdit()}
                                                            className="btn-icon remove-item-btn" >
                                                            <i className="la la-minus-circle" />
                                                        </button>
                                                        <span>{adj.partNumber}</span>
                                                    </td>
                                                    <td style={{ width: '10%' }}>

                                                        <select
                                                            id="serviceCenter"
                                                            className="form-control" 
                                                            onChange={(e: any) => handleServiceCenterChange(e, grpIdx, adjIdx)}
                                                            disabled={disableEdit()}
                                                            value={adj.serviceCenterId}>
                                                            {
                                                                serviceCenters.map((sc: ServiceCenter) => (
                                                                    <option key={sc.name} value={sc.name}>{sc.name}</option>
                                                                ))
                                                            }
                                                            </select>
                                                    </td>
                                                    <td style={{ width: '18%' }}>
                                                        <span>{adj.description}</span>
                                                    </td>
                                                    <td style={{ width: '10%' }}>
                                                        <span>{adj.unitOfMeasure}</span>
                                                    </td>
                                                    <td style={{ width: '8%' }}>
                                                        <input
                                                            id="quantity1"
                                                            disabled={disableEdit()}
                                                            className="form-control"
                                                            name="quantity1"
                                                            value={adj.quantity1}
                                                            onChange={(e) => handleChange(e, grpIdx, adjIdx)}
                                                            onBlur={(e) => handleBlur(e, grpIdx, adjIdx)}
                                                        />
                                                    </td>
                                                    <td style={{ width: '8%' }}>
                                                        <input
                                                            id="quantity2"
                                                            disabled={disableEdit()}
                                                            className="form-control"
                                                            name="quantity2"
                                                            value={adj.quantity2}
                                                            onChange={(e) => handleChange(e, grpIdx, adjIdx)}
                                                            onBlur={(e) => handleBlur(e, grpIdx, adjIdx)}
                                                        />
                                                    </td>
                                                    <td style={{ width: '8%' }}>
                                                        <input
                                                            id="quantity3"
                                                            disabled={disableEdit()}
                                                            className="form-control"
                                                            name="quantity3"
                                                            value={adj.quantity3}
                                                            onChange={(e) => handleChange(e, grpIdx, adjIdx)}
                                                            onBlur={(e) => handleBlur(e, grpIdx, adjIdx)}
                                                        />
                                                    </td>
                                                    <td style={{ width: '8%' }}>
                                                        <input
                                                            id="quantity4"
                                                            disabled={disableEdit()}
                                                            className="form-control"
                                                            name="quantity4"
                                                            value={adj.quantity4}                                                            
                                                            onChange={(e) => handleChange(e, grpIdx, adjIdx)}
                                                            onBlur={(e) => handleBlur(e, grpIdx, adjIdx)}
                                                        />
                                                    </td>
                                                    <td style={{ width: '8%' }}>
                                                        <input
                                                            id="quantity5"
                                                            disabled={disableEdit()}
                                                            className="form-control"
                                                            name="quantity5"
                                                            value={adj.quantity5}
                                                            onChange={(e) => handleChange(e, grpIdx, adjIdx)}
                                                            onBlur={(e) => handleBlur(e, grpIdx, adjIdx)}
                                                        />
                                                    </td>
                                                    <td style={{ width: '8%' }}>
                                                        <input
                                                            id="quantity6"
                                                            disabled={disableEdit()}
                                                            className="form-control"
                                                            name="quantity6"
                                                            value={adj.quantity6}
                                                            onChange={(e) => handleChange(e, grpIdx, adjIdx)}
                                                            onBlur={(e) => handleBlur(e, grpIdx, adjIdx)}
                                                        />
                                                    </td>
                                                </tr>
                                            })
                                        }
                                        <tr>
                                            <td colSpan={2}>
                                                <button
                                                    id="add-item"
                                                    disabled={disableEdit()}
                                                    onClick={() => addAdjustment(grpIdx)}
                                                    type="button"
                                                    className="btn-icon add-item-btn" >
                                                    <i className="la la-plus-circle" />
                                                    Add Item
                                                </button>
                                            </td>
                                            <td colSpan={2}>
                                                <AutoComplete
                                                    id="part-autocomplete"
                                                    disabled={disableEdit()}
                                                    dispatch={dispatch}
                                                    onSelectedCallback={onPartSelected}
                                                    placeholder="Part #"
                                                    currentValue={newPart ? newPart.partNumber : ''}
                                                    suggestionRender={'partNumber'}
                                                    endpoint={ApiEndpoints.INVENTORY_PARTS_AUTOCOMPLETE.formattify(getReportId())}
                                                />
                                            </td>
                                        </tr>
                                    </Table>
                                </div>
                            </div>
                        </div>
                    </div>
                })
            }
        </PageBody>
    );
});